import { ApolloLink } from '@apollo/client';
import { Observable, Observer, Subscription } from 'rxjs';

interface QueuedOperation {
  operation: ApolloLink.Operation;
  forward: ApolloLink.ForwardFunction;
  observer: Observer<ApolloLink.Result>;
  subscription?: Subscription;
}

/** Hold operations while offline and forward them in their original order once online. */
export default class QueueLink extends ApolloLink {
  private opQueue: QueuedOperation[] = [];
  private isOpen = true;

  open(): void {
    this.isOpen = true;
    const queued = this.opQueue;
    this.opQueue = [];
    queued.forEach((entry) => this.forward(entry));
  }

  close(): void {
    this.isOpen = false;
  }

  request(
    operation: ApolloLink.Operation,
    forward: ApolloLink.ForwardFunction,
  ): Observable<ApolloLink.Result> {
    if (this.isOpen || operation.getContext().skipQueue) {
      return forward(operation);
    }

    return new Observable((observer) => {
      const entry: QueuedOperation = { operation, forward, observer };
      this.opQueue.push(entry);
      return () => {
        this.opQueue = this.opQueue.filter((queued) => queued !== entry);
        entry.subscription?.unsubscribe();
      };
    });
  }

  private forward(entry: QueuedOperation): void {
    if (entry.subscription?.closed) return;
    entry.subscription = entry
      .forward(entry.operation)
      .subscribe(entry.observer);
  }
}
